import { FC, useMemo, useState } from 'react'
import {
  Box,
  ButtonBase,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  FileDownloadOutlined,
  SearchOutlined,
  VisibilityOutlined,
  PlayCircleOutline
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useIntl } from 'react-intl'

import { useDocumentsList } from 'services/documents/documents.hooks'
import { useLanguage } from 'services/i18n/i18n.hooks'
import { useUserRoles, isOwnerOnly, shouldShowForOwner } from 'services/rbac/useUserRoles'
import { getFaultGradient } from 'components/Helpdesk/helpdesk.utils'

// Filter chips from the Figma design. Category ids must match the backend
// document categories (documents expose a numeric `category` field only).
const DOCUMENT_CATEGORY_FILTERS = [
  {
    value: 'all' as const,
    i18n: { id: 'helpdesk.filterAll', defaultMessage: 'Wszystkie' }
  },
  {
    value: 1,
    i18n: { id: 'helpdesk.filterInstructions', defaultMessage: 'Instrukcje' }
  },
  {
    value: 2,
    i18n: { id: 'helpdesk.filterGuides', defaultMessage: 'Poradniki' }
  },
  {
    value: 3,
    i18n: { id: 'helpdesk.filterInfo', defaultMessage: 'Informacje' }
  }
]

// Gradients rotated across the PDF thumbnails, as in the Figma doc grid
// (navy→cyan, navy→azure, violet→azure).
const THUMB_GRADIENTS_ORDER = [2, 0, 1] as const

// Knowledge base list ("Panel Klienta 3.0" Pomoc tab).
// Desktop: filter chips + 3-column document grid. Mobile: search + rows.
const HelpList: FC = () => {
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const { language } = useLanguage()
  const { data: attachmentsList = [], isFetching, isLoading } = useDocumentsList()
  const userRoles = useUserRoles()
  const isOnlyOwner = isOwnerOnly(userRoles)
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<'all' | number>('all')

  const visibleDocuments = useMemo(
    () =>
      attachmentsList
        .filter(({ displayInHelp }) => displayInHelp)
        .filter((item) => !item.language || item.language.isoCode === language)
        .filter((item) => shouldShowForOwner(isOnlyOwner, item.visibleForOwner))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [attachmentsList, language, isOnlyOwner]
  )

  const attachments = visibleDocuments
    .filter((item) => categoryFilter === 'all' || item.category === categoryFilter)
    .filter((item) => item.name.toLowerCase().includes(search.trim().toLowerCase()))

  if (isFetching || isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  const filterChips = (
    <Box
      sx={{
        display: 'flex',
        gap: '8px',
        overflowX: 'auto',
        flexShrink: 0
      }}>
      {DOCUMENT_CATEGORY_FILTERS.map(({ value, i18n }) => {
        const isActive = categoryFilter === value
        return (
          <ButtonBase
            key={String(value)}
            onClick={() => setCategoryFilter(value)}
            sx={{
              padding: '7px 15px',
              backgroundColor: isActive ? alpha(theme.r4r.navy, 0.08) : '#fff',
              border: `1px solid ${isActive ? theme.r4r.navy : theme.r4r.greyLight}`,
              color: isActive ? theme.r4r.navy : theme.r4r.grey,
              fontFamily: theme.typography.fontFamily,
              fontSize: '12px',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}>
            {formatMessage(i18n)}
          </ButtonBase>
        )
      })}
    </Box>
  )

  const searchField = (
    <TextField
      value={search}
      onChange={(event) => setSearch(event.target.value)}
      size='small'
      placeholder={formatMessage({
        id: 'helpdesk.searchDocument',
        defaultMessage: 'Szukaj dokumentu...'
      })}
      InputProps={{
        startAdornment: (
          <InputAdornment position='start'>
            <SearchOutlined sx={{ fontSize: '18px', color: theme.r4r.grey }} />
          </InputAdornment>
        )
      }}
      sx={{
        width: isDesktop ? '260px' : '100%',
        '.MuiOutlinedInput-root': {
          backgroundColor: '#fff',
          fontSize: '13px',
          borderRadius: 0,
          '& fieldset': { borderColor: theme.r4r.greyLight }
        }
      }}
    />
  )

  if (isDesktop) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px'
          }}>
          {filterChips}
          {searchField}
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: '16px'
          }}>
          {attachments.map(({ name, id, file, movieUrl }, index) => (
            <Box
              key={id}
              onClick={() => navigate(`/help/${id}`)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '14px',
                height: '84px',
                padding: '16px 20px',
                cursor: 'pointer',
                backgroundColor: '#fff',
                boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.06)',
                transition: 'background-color 0.2s',
                ':hover': {
                  backgroundColor: '#F9FAFB'
                }
              }}>
              <Box
                sx={{
                  width: '44px',
                  height: '52px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: getFaultGradient(THUMB_GRADIENTS_ORDER[index % 3])
                }}>
                {movieUrl ? (
                  <PlayCircleOutline sx={{ color: '#fff', fontSize: '22px' }} />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '11px',
                      fontWeight: 800,
                      letterSpacing: '0.5px',
                      color: '#fff'
                    }}>
                    PDF
                  </Typography>
                )}
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.r4r.ink,
                  lineHeight: 'normal',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}>
                {name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <IconButton
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/help/${id}`)
                  }}
                  sx={{ width: '28px', height: '28px', color: theme.r4r.azure }}>
                  <VisibilityOutlined sx={{ fontSize: '20px' }} />
                </IconButton>
                {file && (
                  <IconButton
                    component='a'
                    href={file}
                    target='_blank'
                    rel='noopener noreferrer'
                    download
                    onClick={(event) => event.stopPropagation()}
                    sx={{ width: '28px', height: '28px', color: theme.r4r.azure }}>
                    <FileDownloadOutlined sx={{ fontSize: '20px' }} />
                  </IconButton>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {searchField}
      {filterChips}

      {attachments.map(({ name, id, file, movieUrl }, index) => (
        <Box
          key={id}
          onClick={() => navigate(`/help/${id}`)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            width: '100%',
            cursor: 'pointer',
            padding: '16px',
            backgroundColor: '#fff',
            boxShadow: theme.r4r.cardShadow,
            transition: 'background-color 0.2s',
            ':hover': {
              backgroundColor: '#F9FAFB'
            }
          }}>
          <Box
            sx={{
              width: '48px',
              height: '48px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              background: getFaultGradient(index)
            }}>
            {movieUrl ? (
              <PlayCircleOutline sx={{ color: '#fff', fontSize: '24px' }} />
            ) : (
              <Typography sx={{ fontSize: '14px', fontWeight: 800, color: '#fff' }}>
                PDF
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '14px',
              fontWeight: 600,
              color: theme.r4r.ink,
              lineHeight: 'normal'
            }}>
            {name}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
            <VisibilityOutlined sx={{ color: theme.r4r.azure, fontSize: '22px' }} />
            {file && (
              <IconButton
                component='a'
                href={file}
                target='_blank'
                rel='noopener noreferrer'
                download
                onClick={(event) => event.stopPropagation()}
                sx={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  backgroundColor: 'transparent',
                  color: theme.r4r.azure
                }}>
                <FileDownloadOutlined sx={{ fontSize: '20px' }} />
              </IconButton>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default HelpList
